import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Hourglass } from "lucide-react";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage } from "../../api";
import { ErrorBlock, LoadingBlock, SeverityIcon } from "../../components/primitives";
import { formatMs } from "../../format";
import { isSlowByDesign, statusClass, SLOW_REQUEST_MS } from "./utils";

/**
 * Small building blocks of the Requests and Errors & events tabs: stat tiles, HTTP status / method chips,
 * the "long poll" hint and the loading / error frame of a query.
 */

export type Tone = "neutral" | "warn" | "nok";

/** One number with a label and a detail line; colour only when the number is a problem. */
export function StatTile({
  label,
  value,
  detail,
  tone = "neutral",
  hint,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
  hint?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-md border px-3 py-2.5",
        tone === "nok" && "border-red-500/40 bg-red-500/10",
        tone === "warn" && "border-amber-500/35",
        className,
      )}
      title={hint}
    >
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        {tone !== "neutral" && (
          <SeverityIcon severity={tone === "nok" ? "critical" : "warning"} className="h-3.5 w-3.5" />
        )}
        <span className="min-w-0 break-words">{label}</span>
      </div>
      <div
        className={cn(
          "mt-1 break-words text-xl font-semibold leading-tight tabular-nums",
          tone === "nok" && "text-red-300",
          tone === "warn" && "text-amber-300",
        )}
      >
        {value}
      </div>
      {detail && <div className="mt-1 break-words text-[11px] leading-snug text-muted-foreground">{detail}</div>}
    </div>
  );
}

/** HTTP status: 4xx amber, 5xx red, everything else calm. */
export function StatusChip({ status, className }: { status: number; className?: string }) {
  const { t } = useLocalization();
  const cls = statusClass(status);
  return (
    <span
      title={t(`monitoring.requests.class.${cls}`)}
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded border px-1.5 font-mono text-[11px] tabular-nums",
        cls === "s5" && "border-red-500/40 bg-red-500/15 text-red-300",
        cls === "s4" && "border-amber-500/35 text-amber-300",
        (cls === "s2" || cls === "s3") && "border-border text-muted-foreground",
        className,
      )}
    >
      {status}
    </span>
  );
}

export function MethodChip({ method, className }: { method: string; className?: string }) {
  if (!method) return null;
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded border border-border px-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
        className,
      )}
    >
      {method}
    </span>
  );
}

/** Marks a route that waits by design, so its latency is not read as a problem. */
export function LongPollHint({ className }: { className?: string }) {
  const { t } = useLocalization();
  return (
    <span
      title={t("monitoring.requests.longPollHint")}
      className={cn(
        "inline-flex h-5 shrink-0 items-center gap-1 rounded border border-dashed border-border px-1.5 text-[10px] text-muted-foreground",
        className,
      )}
    >
      <Hourglass className="h-3 w-3" aria-hidden />
      {t("monitoring.requests.longPoll")}
    </span>
  );
}

/** Duration of one request: amber from 2 s on, unless the route waits by design. */
export function Duration({ ms, route, className }: { ms: number; route: string; className?: string }) {
  const slow = ms >= SLOW_REQUEST_MS && !isSlowByDesign(route);
  return (
    <span className={cn("font-mono tabular-nums", slow ? "text-amber-300" : "text-foreground/90", className)}>
      {formatMs(ms)}
    </span>
  );
}

/**
 * Loading / error frame of a query: a skeleton until the first answer, an error with retry when there
 * is nothing to show, and a compact error above the last good data when a later poll fails.
 */
export function QueryBody<T>({
  query,
  height = 160,
  children,
}: {
  query: UseQueryResult<T>;
  height?: number;
  children: (data: T) => ReactNode;
}) {
  if (query.data === undefined) {
    if (query.isError) {
      return <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;
    }
    return <LoadingBlock height={height} />;
  }
  return (
    <>
      {query.isError && (
        <ErrorBlock className="mb-3" message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      )}
      {children(query.data)}
    </>
  );
}
